<template>
  <div v-if="loaded" class="account-settings-container">
    <div v-if="!isEdit" class="content">
      <div class="title">{{ $t('addLevelMonitoring') }}</div>

      <div class="container">
        <div class="form-container">
          <div class="input-title">{{ $t('priorityLevel') }}</div>
          <Dropdown
            class="priority-dropdown small"
            :config="{ inputSize: 48 }"
            :values="[1, 2, 3, 4, 5, 6, 7, 8, 9]"
            :placeholder="$t('chooseMonitoringLevel')"
            :on-change="value => (monitoringLevel = value)"
            :value="monitoringLevel"
          >
            <template #placeholder="{}">
              <MonitoringType v-if="monitoringLevel || monitoringLevel === 0" :config="{ maxElementWidth: 0 }" :level="monitoringLevel" :label="''" />
            </template>
            <template #choices="{ choice }">
              <MonitoringType :config="{ maxElementWidth: 0 }" :class="{ highlight: choice === monitoringLevel }" :level="choice" :label="''" />
            </template>
          </Dropdown>
        </div>
        <div class="form-container">
          <div class="input-title">{{ $t('labelLevelSupervision') }}</div>
          <input v-model="label" type="text" class="input" :class="{ incorrect: !label && isSend }" :placeholder="$t('suggestLabel')" />
        </div>
      </div>

      <div class="form-container nomargin">
        <div class="input-title low-margin">{{ $t('associatedPackage') }}</div>
        <Package :values="packagePossibility" :class="{ incorrect: !monitoringLevel }" :on-change="value => (packageLevel = value)" :value="packageLevel" />
      </div>

      <div class="info">
        <div class="icon" />
        <div class="text">{{ $t('paymentAtMonitoringInfo') }}</div>
      </div>

      <div v-if="(!packageLevel || !monitoringLevel) && isSend" class="info error">
        <div class="icon" />
        <div class="text">{{ $t('packageSelectMonitoringToCreate') }}</div>
      </div>

      <div class="btn-container">
        <div class="main-button cancel" @click="cancel">{{ $t('cancel') }}</div>
        <div class="main-button" :class="{ disabled: !packageLevel || !monitoringLevel }" @click="add">
          {{ $t('add') }}
        </div>
      </div>
    </div>
    <div v-else class="content">
      <div class="title">{{ $t('editLevelMonitoring') }}</div>

      <div class="container">
        <div class="form-container">
          <div class="input-title">{{ $t('priorityLevel') }}</div>
          <Dropdown
            class="priority-dropdown small"
            :config="{ inputSize: 48 }"
            :values="[1, 2, 3, 4, 5, 6, 7, 8, 9]"
            :placeholder="$t('chooseMonitoringLevel')"
            :on-change="
              value => {
                monitoringLevel = value;
                isSend = false;
              }
            "
            :value="monitoringLevel"
          >
            <template #placeholder="{}">
              <MonitoringType v-if="monitoringLevel || monitoringLevel === 0" :config="{ maxElementWidth: 0 }" :level="monitoringLevel" :label="''" />
            </template>
            <template #choices="{ choice }">
              <MonitoringType :config="{ maxElementWidth: 0 }" :class="{ highlight: choice === monitoringLevel }" :level="choice" :label="''" />
            </template>
          </Dropdown>
        </div>
        <div class="form-container">
          <div class="input-title">{{ $t('labelLevelSupervision') }}</div>
          <input v-model="label" type="text" class="input" :placeholder="$t('suggestLabel')" @change="() => (isSend = false)" />
        </div>
      </div>

      <div class="form-container nomargin">
        <div class="input-title low-margin">{{ $t('associatedPackage') }}</div>
        <Package
          :disabled="true"
          :values="packagePossibility.filter(e => e.publicId === packageLevel)"
          :on-change="
            value => {
              packageLevel = value;
              isSend = false;
            }
          "
          :value="packageLevel"
        />
      </div>

      <div class="info">
        <div class="icon" />
        <div class="text">{{ $t('paymentAtMonitoringInfo') }}</div>
      </div>

      <div class="info margin-top">
        <div class="text black pointer" @click.stop="disablePackageLevel">{{ $t('disableMonitoringLevel') }}</div>
        <div v-if="errorDuringDisabling" class="text error">
          Vous ne pouvez pas désactiver ce niveau de surveillance car certaines entreprises de votre portefeuille l'utilisent
        </div>
      </div>

      <div class="info">
        <div class="icon"></div>
        <div class="text">{{ $t('accountSettingsInfoMonitoring') }}</div>
      </div>

      <div class="btn-container">
        <div class="main-button cancel" @click="cancel">{{ $t('cancel') }}</div>
        <div class="main-button" @click="edit">{{ $t('change') }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Dropdown from '@/components/Dropdown';
import MonitoringType from '@/components/monitoringType';
import Package from '@/components/Package';

export default {
  components: { Dropdown, Package, MonitoringType },

  beforeRouteLeave(to, from, next) {
    if (this.isSend || !this.packageLevel || !this.monitoringLevel) return next();
    if (
      (!(this.packageLevel === this.$route.query.level) || !(this.monitoringLevel !== this.$route.query.label) || !(this.label !== this.$route.query.label)) &&
      confirm(this.$t('areYouSureToExit'))
    )
      return next();
    else if (this.packageLevel === this.$route.query.level && this.monitoringLevel !== this.$route.query.label && this.label !== this.$route.query.label)
      return next();
  },

  props: {
    isEdit: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      packageLevel: '',
      monitoringLevel: 1,
      label: '',
      isEnabled: true,
      loading: false,
      error: false,
      errorMessage: null,
      packagePossibility: [],
      packages: [],
      loaded: false,
      errorDuringDisabling: false,
      isSend: false,
    };
  },

  computed: {
    ...mapGetters(['user']),

    getMonitoringInfos() {
      const info = this.packages.find(value => value.publicId === this.monitoringLevel);
      return info;
    },
  },

  async mounted() {
    this.packages = (await this.$store.dispatch('getPackagesLevel')).items;
    await this.getPackages();
    if (this.$route.query.id) {
      this.label = this.$route.query.label;
      this.monitoringLevel = this.$route.query.level;
      this.isEnabled = this.$route.query.isEnabled;
    } else if (!this.edit) {
      this.monitoringLevel = this.packages[0].publicId;
    }
    this.loaded = true;
  },

  methods: {
    async cancel() {
      this.isSend = true;
      this.$router.go(-1);
    },

    async disablePackageLevel() {
      const result = await this.$store.dispatch('setPopup', { popupType: 'disableMonitoringLevel' });
      if (result) {
        this.isEnabled = !this.isEnabled;
        this.edit();
      }
    },

    async add() {
      this.isSend = true;
      if (!this.packageLevel || !this.monitoringLevel || !this.label) {
        this.error = true;
        return;
      }
      await this.$store.dispatch('addPackage', {
        packagePublicId: this.packageLevel,
        label: this.label,
        level: this.monitoringLevel,
      });
      this.$router.go(-1);
    },

    async edit() {
      this.isSend = true;
      try {
        await this.$store.dispatch('editPackage', {
          id: this.$route.query.id,
          label: this.$route.query.label === this.label ? undefined : this.label,
          level: this.monitoringLevel,
          isEnabled: this.isEnabled,
        });
        this.$router.go(-1);
      } catch (error) {
        if (error.response.status === 409) {
          this.errorDuringDisabling = true;
        }
      }
    },

    async getPackages() {
      const result = (await this.$store.dispatch('getPackages')).items;
      this.packagePossibility = result;
      if (this.$route.query.packageId) this.packageLevel = result.find(e => e.publicId === this.$route.query.packageId).publicId;
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
@use '@/styles/fonts.scss';
.account-settings-container {
  margin: 60px 0 0;

  .content {
    .title {
      font-family: fonts.$title-font;
      color: colors.$darkblue-color;
      font-weight: 500;
      font-size: 22px;
      text-align: left;
    }
    .container {
      display: flex;
      align-items: flex-start;
      justify-content: flex-start;
    }

    .form-container {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      margin-right: 80px;

      &.nomargin {
        margin: 0;
      }

      .input-title {
        color: colors.$darkblue-color;
        font-weight: 500;
        font-size: 16px;
        margin: 46px 0 10px 0;

        &.low-margin {
          margin: 20px 0 10px 0;
        }
      }

      .input {
        width: 100%;
        max-width: 500px;
        height: 48px;
        margin: 0 0 16px;
        padding: 12px 16px;
        box-sizing: border-box;
        color: colors.$text-color;
        border: solid 1px colors.$lightgrey-color;
        border-radius: 4px;
        background: colors.$white-color;

        &:last-child {
          margin: 0;
        }

        &::placeholder {
          color: colors.$lightgrey-color;
        }

        &.incorrect {
          border-color: colors.$error-color;
        }
      }

      .priority-dropdown {
        width: 180px;

        &.incorrect {
          border-color: colors.$error-color;
        }

        &.small {
          width: 80px;
        }
      }
    }

    .info {
      display: flex;
      align-items: center;
      margin: 14px 0 0 0;
      font-size: 14px;

      &.margin-top {
        margin: 64px 0 0;
        flex-direction: column;
        align-items: flex-start;

        .error {
          color: colors.$red-color;
          margin-bottom: 16px;
        }
      }

      .icon {
        margin: 0 12px 0 0;
        position: relative;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        border: 2px solid colors.$lightblue-color;
        flex-shrink: 0;

        &:after {
          position: absolute;
          content: '';
          bottom: 3px;
          left: calc(50% - 1px);
          right: calc(50% - 1px);
          height: 8px;
          background: colors.$lightblue-color;
        }

        &:before {
          position: absolute;
          content: '';
          top: 3px;
          left: calc(50% - 1px);
          right: calc(50% - 1px);
          height: 2px;
          background: colors.$lightblue-color;
        }
      }

      .text {
        color: colors.$lightblue-color;
        font-size: 14px;
        opacity: 0.8;
        &.black {
          color: colors.$black-color;
          margin-bottom: 14px;
          font-weight: 500;
          font-size: 14px;
          line-height: 16px;
          display: flex;
          align-items: center;
          text-decoration-line: underline;

          &.pointer {
            cursor: pointer;
          }
        }
      }
      &.error {
        .text {
          color: colors.$red-color !important;
        }
        .icon {
          border-color: colors.$red-color;

          &:after {
            background: colors.$red-color;
          }

          &:before {
            background: colors.$red-color;
          }
        }
      }
    }

    .btn-container {
      display: flex;
      margin: 80px 0 24px;

      .main-button {
        margin: 0 12px 0;
        width: 200px;
        padding: 0;

        &:not(.disabled):hover {
          cursor: pointer;
          transform: scale(1);
        }

        &.cancel {
          margin: 0 12px 0 0;
          background: colors.$lightgrey-color;
          color: colors.$darkgrey-color;
        }
      }
    }
  }
}

@media only screen and (max-width: responsive.$layout-breakpoint-medium) {
  .container {
    flex-direction: column;
  }

  .btn-container {
    flex-direction: column;
    align-items: center;

    margin-top: 36px !important;

    .main-button {
      margin: 6px 0 !important;
    }
  }
}
</style>
